import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export const getConversations = async (replikerId) => {
  try {
    const response = await axios.get(
      `${API_URL}/api/chat/conversations/${replikerId}`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

export const getDashboardConversations = async (replikerId) => {
  try {
    const response = await axios.get(
      // Usar el nuevo endpoint específico para dashboards
      `${API_URL}/api/chat/dashboard/conversations/${replikerId}`,
      { withCredentials: true },
    );
    // Debe devolver el historial completo del Repliker, ordenado por más reciente.
    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard conversations:", error);
    // Si falla (ej: no es dueño), devolver un array vacío o manejar el error
    throw error;
  }
};

export const createConversation = async (replikerId, name) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/chat/conversations`,
      { replikerId, name },
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error creating conversation:", error);
    throw error;
  }
};

export const deleteConversation = async (conversationId) => {
  try {
    await axios.delete(`${API_URL}/api/chat/conversations/${conversationId}`, {
      withCredentials: true,
    });
  } catch (error) {
    console.error("Error deleting conversation:", error);
    throw error;
  }
};

export const updateConversation = async (conversationId, name, history = null) => {
  try {
    const payload = {};
    if (name) payload.name = name;
    if (history) payload.history = history;

    const response = await axios.put(
      `${API_URL}/api/chat/conversations/${conversationId}`,
      payload,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating conversation:", error);
    throw error;
  }
};

export const sendMessage = async (
  message,
  replikerId,
  conversationId,
  signal,
  customApiUrl = null, // Optional: for dynamic replikers
) => {
  try {
    // Use custom API URL for dynamic replikers, otherwise use default backend
    const targetUrl = customApiUrl || `${API_URL}/api/chat/conversation`;

    // Prepare payload based on target
    const payload = customApiUrl
      ? { message, thread_id: conversationId } // Dynamic agents use thread_id
      : { message, replikerId, conversationId }; // Backend proxy uses these

    const response = await axios.post(
      targetUrl,
      payload,
      {
        withCredentials: !customApiUrl, // False if dynamic agent (external), True if backend
        signal
      },
    );
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled:", error.message);
    } else {
      console.error("Error sending message:", error);
    }
    throw error;
  }
};

export const sendMessageStream = async (
  message,
  replikerId,
  conversationId,
  { signal, onToken, onDone, onError, onStatus } = {},
) => {
  try {
    const response = await fetch(`${API_URL}/api/chat/conversation/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ message, replikerId, conversationId }),
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Streaming request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentEvent = "message";
    let currentDataLines = [];
    let donePayload = null;
    let doneReceived = false;

    const dispatchEvent = () => {
      if (!currentDataLines.length) {
        currentEvent = "message";
        return;
      }
      const rawData = currentDataLines.join("\n");
      let data;
      try {
        data = JSON.parse(rawData);
      } catch {
        currentEvent = "message";
        currentDataLines = [];
        return;
      }

      if (currentEvent === "token") {
        const token = typeof data?.token === "string" ? data.token : "";
        if (token && onToken) onToken(token);
      } else if (currentEvent === "status") {
        const status = typeof data?.status === "string" ? data.status : "";
        if (status && onStatus) onStatus(status);
      } else if (currentEvent === "ping") {
        console.log("[Client SSE] Keep-alive ping received:", data);
      } else if (currentEvent === "done") {
        donePayload = data;
        doneReceived = true;
        if (onDone) onDone(data);
      } else if (currentEvent === "error" && onError) {
        onError(data);
      }
      currentEvent = "message";
      currentDataLines = [];
    };

    const processIncoming = (chunkText) => {
      buffer += chunkText;
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";

      lines.forEach((line) => {
        if (line.startsWith(":")) return;
        if (line === "") {
          dispatchEvent();
          return;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim() || "message";
          return;
        }
        if (line.startsWith("data:")) {
          currentDataLines.push(line.slice(5).trim());
        }
      });
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      processIncoming(decoder.decode(value, { stream: true }));
      if (doneReceived) {
        try {
          await reader.cancel();
        } catch (_) {
          // no-op
        }
        break;
      }
    }

    if (buffer.trim() && !doneReceived) {
      processIncoming("\n");
    }
    dispatchEvent();

    return donePayload;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
};

export const sendFileMessage = async (
  file,
  message,
  replikerId,
  conversationId,
  signal,
  customApiUrl = null, // Optional: for dynamic replikers
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (message) {
    formData.append("message", message);
  }

  // HERENCIA TOTAL: Siempre usamos el backend de Node para archivos
  // Esto garantiza que el archivo se suba a GCS y se guarde en Postgres (PERSISTENCIA)
  const targetUrl = `${API_URL}/api/chat/upload`;

  formData.append("replikerId", replikerId);
  formData.append("conversationId", conversationId);

  // Si es un agente dinámico, pasamos su API URL para que Node sepa dónde reenviar
  if (customApiUrl) {
    formData.append("customApiUrl", customApiUrl);
  }

  try {
    const response = await axios.post(targetUrl, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true, // SIEMPRE true porque vamos a nuestro backend
      signal,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("File upload canceled:", error.message);
    } else {
      console.error("Error uploading file:", error);
    }
    throw error;
  }
};

export const sendFileMessageStream = async (
  file,
  message,
  replikerId,
  conversationId,
  { signal, onStatus, onToken, onDone, onError } = {},
  customApiUrl = null,
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (message) formData.append("message", message);
  formData.append("replikerId", replikerId);
  formData.append("conversationId", conversationId);
  if (customApiUrl) formData.append("customApiUrl", customApiUrl);

  try {
    const response = await fetch(`${API_URL}/api/chat/upload/stream`, {
      method: "POST",
      credentials: "include",
      body: formData,
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`File stream failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentEvent = "message";
    let currentDataLines = [];
    let donePayload = null;
    let doneReceived = false;

    const dispatchEvent = () => {
      if (!currentDataLines.length) {
        currentEvent = "message";
        return;
      }
      const rawData = currentDataLines.join("\n");
      let data;
      try {
        data = JSON.parse(rawData);
      } catch {
        currentEvent = "message";
        currentDataLines = [];
        return;
      }

      if (currentEvent === "status") {
        const status = typeof data?.status === "string" ? data.status : "";
        if (status && onStatus) onStatus(status);
      } else if (currentEvent === "ping") {
        console.log("[Client SSE] Keep-alive ping received:", data);
      } else if (currentEvent === "token") {
        const token = typeof data?.token === "string" ? data.token : "";
        if (token && onToken) onToken(token);
      } else if (currentEvent === "done") {
        console.log("[Client SSE] DONE event received! Payload history length:", data?.history?.length);
        donePayload = data;
        doneReceived = true;
        if (onDone) onDone(data);
      } else if (currentEvent === "error" && onError) {
        console.error("[Client SSE] Error event received:", data);
        onError(data);
      }
      currentEvent = "message";
      currentDataLines = [];
    };

    const processIncoming = (chunkText) => {
      buffer += chunkText;
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";

      lines.forEach((line) => {
        if (line.startsWith(":")) return;
        if (line === "") {
          dispatchEvent();
          return;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim() || "message";
          return;
        }
        if (line.startsWith("data:")) {
          currentDataLines.push(line.slice(5).trim());
        }
      });
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      processIncoming(decoder.decode(value, { stream: true }));
      if (doneReceived) {
        try {
          await reader.cancel();
        } catch (_) {
          // no-op
        }
        break;
      }
    }

    if (buffer.trim() && !doneReceived) {
      processIncoming("\n");
    }
    dispatchEvent();

    return donePayload;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
};

export const sendRepliMessage = async (message, agentType = "default", userId = null, threadId = "default-thread") => {
  try {
    // Comentamos la llamada directa para que pase por el backend de Node.js 
    // y así el historial se guarde en PostgreSQL correctamente.
    /*
    if (agentType === "repli-insights") {
      const response = await axios.post(
        "http://localhost:8090/api/conversation",
        { 
          message, 
          user_id: userId, 
          thread_id: threadId || "insights-thread" 
        }
      );
      
      const botMessage = typeof response.data === 'string' 
        ? response.data 
        : response.data.message || response.data.answer || JSON.stringify(response.data);

      return {
        history: [
          { sender: "user", text: message },
          { sender: "bot", text: botMessage }
        ]
      };
    }
    */

    const response = await axios.post(
      `${API_URL}/api/chat/repli-conversation`,
      { message, agentType },
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error sending message to Repli:", error);
    throw error;
  }
};

export const sendRepliMessageStream = async (
  message,
  agentType = "default",
  { signal, onToken, onDone, onError, onStatus } = {},
) => {
  try {
    const response = await fetch(`${API_URL}/api/chat/repli-conversation/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ message, agentType }),
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Streaming request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentEvent = "message";
    let currentDataLines = [];
    let donePayload = null;
    let doneReceived = false;

    const dispatchEvent = () => {
      if (!currentDataLines.length) {
        currentEvent = "message";
        return;
      }
      const rawData = currentDataLines.join("\n");
      let data;
      try {
        data = JSON.parse(rawData);
      } catch {
        currentEvent = "message";
        currentDataLines = [];
        return;
      }

      if (currentEvent === "token") {
        const token = typeof data?.token === "string" ? data.token : "";
        if (token && onToken) onToken(token);
      } else if (currentEvent === "status") {
        const status = typeof data?.status === "string" ? data.status : "";
        if (status && onStatus) onStatus(status);
      } else if (currentEvent === "ping") {
        console.log("[Client SSE] Keep-alive ping received:", data);
      } else if (currentEvent === "done") {
        donePayload = data;
        doneReceived = true;
        if (onDone) onDone(data);
      } else if (currentEvent === "error" && onError) {
        onError(data);
      }
      currentEvent = "message";
      currentDataLines = [];
    };

    const processIncoming = (chunkText) => {
      buffer += chunkText;
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";

      lines.forEach((line) => {
        if (line.startsWith(":")) return;
        if (line === "") {
          dispatchEvent();
          return;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim() || "message";
          return;
        }
        if (line.startsWith("data:")) {
          currentDataLines.push(line.slice(5).trim());
        }
      });
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      processIncoming(decoder.decode(value, { stream: true }));
      if (doneReceived) {
        try {
          await reader.cancel();
        } catch (_) {
          // no-op
        }
        break;
      }
    }

    if (buffer.trim() && !doneReceived) {
      processIncoming("\n");
    }
    dispatchEvent();

    return donePayload;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
};

export const sendRepliFileMessage = async (
  file,
  message,
  agentType = "default",
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (message) {
    formData.append("message", message);
  }
  formData.append("agentType", agentType);

  try {
    const response = await axios.post(
      `${API_URL}/api/chat/repli-conversation/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading file to Repli:", error);
    throw error;
  }
};

export const getRepliPostHistory = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/api/chat/repli-conversation/history`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Repli Post history:", error);
    throw error;
  }
};

export const clearRepliPostHistory = async () => {
  try {
    const response = await axios.delete(
      `${API_URL}/api/chat/repli-conversation/history`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error clearing Repli Post history:", error);
    throw error;
  }
};

export const getRepliInsightsHistory = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/api/chat/repli-conversation/history-insights`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Repli Insights history:", error);
    throw error;
  }
};

export const clearRepliInsightsHistory = async () => {
  try {
    const response = await axios.delete(
      `${API_URL}/api/chat/repli-conversation/history-insights`,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    console.error("Error clearing Repli Insights history:", error);
    throw error;
  }
};
